import React from 'react';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
  Drawer,
  Box,
  Avatar,
  Divider,
  alpha,
  Paper
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Quiz as QuizIcon,
  Groups as GroupsIcon,
  EmojiEvents as AchievementsIcon,
  Logout as LogoutIcon,
  School as SchoolIcon
} from '@mui/icons-material';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { APP_CHROME_OFFSET } from 'theme/chromeOffset';

const menuItems = [
  { header: 'Overview' },
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/student/v2' },

  { header: 'Learning' },
  { text: 'Sessions', icon: <SchoolIcon />, path: '/sessions' },
  { text: 'Quizzes', icon: <QuizIcon />, path: '/student/v2/quizzes' },

  { header: 'Progress' },
  { text: 'Achievements', icon: <AchievementsIcon />, path: '/student/v2/achievements' },
  { text: 'Teams', icon: <GroupsIcon />, path: '/student/teams' },
];

interface StudentSidebarProps {
  open: boolean;
  toggle: () => void;
  drawerWidth: number;
  collapsedWidth: number;
}

const StudentSidebar: React.FC<StudentSidebarProps> = ({ open, drawerWidth, collapsedWidth }) => {
  const theme = useTheme();
  const location = useLocation();
  const { logout, authState } = useAuth();
  const user = authState.user;

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Logout failed", error);
    }
  };

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header / Logo Area */}
      <Box sx={{
        p: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: open ? 'flex-start' : 'center',
        minHeight: 80
      }}>
        <Avatar
          sx={{
            bgcolor: theme.palette.primary.main,
            width: 40,
            height: 40,
            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`
          }}
        >
          MW
        </Avatar>
        {open && (
          <Box sx={{ ml: 2, overflow: 'hidden' }}>
            <Typography variant="h6" fontWeight="bold" noWrap>
              MathWiz
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              Student Portal
            </Typography>
          </Box>
        )}
      </Box>

      <Divider sx={{ mb: 2, mx: 3, opacity: 0.5 }} />

      {/* Navigation Items */}
      <List sx={{ flexGrow: 1, px: 2, overflowY: 'auto' }}>
        {menuItems.map((item, index) => {
          if (item.header) {
            return open ? (
              <Typography
                key={`header-${index}`}
                variant="caption"
                color="text.secondary"
                fontWeight="bold"
                sx={{ px: 2, mt: 3, mb: 1, display: 'block', textTransform: 'uppercase', letterSpacing: 1 }}
              >
                {item.header}
              </Typography>
            ) : <Divider key={`divider-${index}`} sx={{ my: 2, opacity: 0.5 }} />;
          }

          const path = item.path || '';
          const isActive = location.pathname === path || (path !== '/student/v2' && location.pathname.startsWith(path));

          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                component={NavLink}
                to={path}
                sx={{
                  borderRadius: 2,
                  minHeight: 48,
                  justifyContent: open ? 'initial' : 'center',
                  px: 2.5,
                  bgcolor: isActive ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                  color: isActive ? theme.palette.primary.main : 'text.secondary',
                  '&:hover': {
                    bgcolor: isActive ? alpha(theme.palette.primary.main, 0.15) : alpha(theme.palette.action.hover, 0.05),
                    color: isActive ? theme.palette.primary.main : 'text.primary',
                  }
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 2 : 'auto',
                    justifyContent: 'center',
                    color: 'inherit'
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{ fontWeight: isActive ? 600 : 500, fontSize: '0.95rem' }}
                  sx={{ opacity: open ? 1 : 0 }}
                />
                {isActive && open && (
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      ml: 1
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      {/* User Profile / Logout Area */}
      <Box sx={{ p: 2, mt: 'auto' }}>
        <Paper
          elevation={0}
          sx={{
            p: open ? 2 : 1,
            bgcolor: alpha(theme.palette.background.default, 0.5),
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: open ? 'space-between' : 'center',
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              bgcolor: alpha(theme.palette.background.default, 0.8),
              borderColor: theme.palette.primary.main
            }
          }}
          onClick={handleLogout}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: user?.profile?.profileColor || theme.palette.primary.main,
                fontSize: '0.875rem'
              }}
            >
              {user?.firstName?.[0]}{user?.lastName?.[0]}
            </Avatar>
            {open && (
              <Box sx={{ ml: 1.5, overflow: 'hidden' }}>
                <Typography variant="subtitle2" fontWeight="bold" noWrap>
                  {user ? `${user.firstName} ${user.lastName}` : 'Student'}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  Sign Out
                </Typography>
              </Box>
            )}
          </Box>
          {open && <LogoutIcon fontSize="small" color="action" />}
        </Paper>
      </Box>
    </Box>
  );

  return (
    <Drawer
      variant="permanent"
      open={open}
      sx={{
        width: open ? drawerWidth : collapsedWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: open ? drawerWidth : collapsedWidth,
          boxSizing: 'border-box',
          borderRight: 'none',
          bgcolor: 'background.paper',
          boxShadow: '4px 0 24px rgba(0,0,0,0.02)',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          overflowX: 'hidden',
          // MUI's permanent Drawer resolves `top`/`height` against the viewport. Offsetting below
          // any global chrome (see theme/chromeOffset.ts) keeps this from rendering underneath it;
          // the var is unset outside the sales demo, so this is a no-op there.
          top: APP_CHROME_OFFSET,
          height: `calc(100% - ${APP_CHROME_OFFSET})`,
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default StudentSidebar;
